// src/pages/vmix.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';

// 🔁 Hook mejorado: fade + zoom + blur + orden aleatorio
const useDynamicCarousel = (items, baseDuration = 3500, vipBonus = 5000) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionState, setTransitionState] = useState('enter'); // 'enter' | 'active' | 'exit'
  const [shuffledItems, setShuffledItems] = useState([]);

  // 🎲 Barajar al inicio y cuando cambie la lista
  useEffect(() => {
    if (items.length === 0) {
      setShuffledItems([]);
      return;
    }
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setShuffledItems(shuffled);
    setCurrentIndex(0);
  }, [items]);

  // 🕒 Temporizador con transición suave (700ms)
  useEffect(() => {
    if (shuffledItems.length === 0) return;

    const currentItem = shuffledItems[currentIndex];
    const isVIP = currentItem?.ticketCount >= 3;
    const duration = isVIP ? baseDuration + vipBonus : baseDuration;

    // Iniciar transición de salida
    const startExit = setTimeout(() => {
      setTransitionState('exit');
    }, duration - 700);

    // Cambiar ítem y resetear estado
    const switchItem = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % shuffledItems.length);
      setTransitionState('enter');
    }, duration);

    return () => {
      clearTimeout(startExit);
      clearTimeout(switchItem);
    };
  }, [currentIndex, shuffledItems, baseDuration, vipBonus]);

  const current = shuffledItems.length > 0 ? shuffledItems[currentIndex] : null;

  // ✅ Estados de transformación para animación fluida
  const transformStyles = useMemo(() => {
    switch (transitionState) {
      case 'enter':
        return {
          opacity: 0,
          transform: 'scale(0.98)',
          filter: 'blur(4px)',
        };
      case 'active':
        return {
          opacity: 1,
          transform: 'scale(1)',
          filter: 'blur(0)',
        };
      case 'exit':
        return {
          opacity: 0,
          transform: 'scale(0.98)',
          filter: 'blur(4px)',
        };
      default:
        return {};
    }
  }, [transitionState]);

  // Activar estado 'active' después del fade-in
  useEffect(() => {
    if (transitionState === 'enter') {
      const timer = setTimeout(() => setTransitionState('active'), 50);
      return () => clearTimeout(timer);
    }
  }, [transitionState]);

  return { current, transformStyles };
};

// 🎨 Fondo dinámico mejorado
const getBackground = () => `
  radial-gradient(circle at 10% 20%, rgba(255,213,79,0.08) 0%, transparent 50%),
  radial-gradient(circle at 90% 80%, rgba(76,175,80,0.12) 0%, transparent 60%),
  linear-gradient(135deg, #082312 0%, #104a22 30%, #1b5e20 60%, #0d3b1a 100%)
`;

const VmixDisplay = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadApproved = async () => {
      try {
        const q = query(collection(db, 'granRifa'), where('isApproved', '==', true));
        const snapshot = await getDocs(q);
        
        const grouped = snapshot.docs.reduce((acc, doc) => {
          const { username, userPhotoUrl } = doc.data();
          const key = (username || 'Anónimo').trim();
          if (!acc[key]) {
            acc[key] = { username: key, ticketCount: 0, userPhotoUrl: userPhotoUrl || null };
          }
          acc[key].ticketCount += 1;
          if (!acc[key].userPhotoUrl && userPhotoUrl) acc[key].userPhotoUrl = userPhotoUrl;
          return acc;
        }, {});

        setParticipants(Object.values(grouped));
      } catch (err) {
        console.error('Error:', err);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };

    loadApproved();
    const interval = setInterval(loadApproved, 20000);
    return () => clearInterval(interval);
  }, []);

  const { current, transformStyles } = useDynamicCarousel(participants, 3500, 5000);
  const isVIP = current?.ticketCount >= 3;

  const renderPhoto = () => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="h2" sx={{ 
        color: isVIP ? '#ffd54f' : '#a5d6a7',
        fontWeight: 'bold',
        textShadow: isVIP ? '0 0 10px rgba(255,213,79,0.6)' : 'none'
      }}>
        ?
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          width: 531,
          height: 983,
          background: getBackground(),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#e0f2f1',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold',
            textShadow: '0 0 12px rgba(76,175,80,0.7)',
            textAlign: 'center',
            px: 3,
          }}
        >
          🔍 Cargando participantes...
        </Typography>
      </Box>
    );
  }

  if (!current) {
    return (
      <Box
        sx={{
          width: 531,
          height: 983,
          background: getBackground(),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          px: 2,
          color: '#e0f2f1',
        }}
      >
        <Box sx={{ maxWidth: '80%' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, textShadow: '0 0 10px rgba(255,213,79,0.5)' }}>
            🎉 ¡Prepárate!
          </Typography>
          <Typography variant="body1">
            La rueda de participantes comenzará en breve.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 531,
        height: 983,
        background: getBackground(),
        color: '#fff',
        overflow: 'hidden',
        position: 'relative',
        // ✨ Partículas sutiles (más naturales)
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 10% 5%, rgba(255,255,255,0.3) 1px, transparent 1px),
            radial-gradient(circle at 95% 90%, rgba(255,255,255,0.25) 1px, transparent 1px),
            radial-gradient(circle at 40% 70%, rgba(144,202,249,0.15) 1px, transparent 1px)
          `,
          backgroundSize: '140px 140px',
          pointerEvents: 'none',
          opacity: 0.25,
        },
      }}
    >
      {/* ✅ Contenedor con transición avanzada */}
      <Box
        key={current.username} // 🔑 Forzar recreación limpia al cambiar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          px: 3,
          width: '100%',
          // 🌊 Transición mejorada: fade + zoom + blur
          opacity: transformStyles.opacity,
          transform: `translate(-50%, -50%) ${transformStyles.transform}`,
          filter: transformStyles.filter,
          transition: 'opacity 0.7s ease, transform 0.7s ease, filter 0.7s ease',
          willChange: 'opacity, transform, filter',
        }}
      >
        {/* 🖼️ Marco principal */}
        <Box
          sx={{
            mb: 3,
            width: isVIP ? 260 : 200,
            height: isVIP ? 260 : 200,
            position: 'relative',
            margin: '0 auto',
          }}
        >
          {/* 🌊 Efecto de onda de energía (solo VIP) */}
          {isVIP && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '4px solid transparent',
                background: 'conic-gradient(from 0deg, transparent, #ffd54f, #4caf50, #81c784, transparent)',
                backgroundClip: 'border-box',
                zIndex: 1,
                animation: 'pulseWave 3s ease-in-out infinite',
                '@keyframes pulseWave': {
                  '0%, 100%': { 
                    background: 'conic-gradient(from 0deg, transparent, #ffd54f, #4caf50, #81c784, transparent)',
                    transform: 'scale(1)',
                  },
                  '50%': { 
                    background: 'conic-gradient(from 180deg, transparent, #81c784, #4caf50, #ffd54f, transparent)',
                    transform: 'scale(1.03)',
                  },
                },
              }}
            />
          )}

          {/* 📸 Foto fija con brillo interno */}
          <Box
            sx={{
              position: 'absolute',
              top: isVIP ? 4 : 4,
              left: isVIP ? 4 : 4,
              right: isVIP ? 4 : 4,
              bottom: isVIP ? 4 : 4,
              borderRadius: '50%',
              overflow: 'hidden',
              zIndex: 2,
              boxShadow: isVIP
                ? '0 0 25px rgba(76,175,80,0.5), inset 0 0 12px rgba(0,0,0,0.4)'
                : '0 0 12px rgba(129,199,132,0.4)',
              border: isVIP
                ? '2px solid rgba(255,255,255,0.2)'
                : '2px solid rgba(129,199,132,0.6)',
            }}
          >
            {current.userPhotoUrl ? (
              <Box
                component="img"
                src={current.userPhotoUrl}
                alt={current.username}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : renderPhoto()}

            {/* ✨ Brillo interno pulsante (solo VIP) */}
            {isVIP && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,213,79,0.4) 0%, transparent 70%)',
                  animation: 'innerGlow 4s ease-in-out infinite',
                  '@keyframes innerGlow': {
                    '0%, 100%': { opacity: 0.6 },
                    '50%': { opacity: 1 },
                  },
                }}
              />
            )}
          </Box>

          {/* ✨ Badge VIP (sin rotación, con pulso) */}
          {isVIP && (
            <Box
              sx={{
                position: 'absolute',
                top: -14,
                right: -14,
                bgcolor: 'rgba(255,213,79,0.97)',
                color: '#e65100',
                borderRadius: '50%',
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 3px 12px rgba(0,0,0,0.4), 0 0 20px rgba(255,213,79,0.7)',
                zIndex: 3,
                animation: 'badgePulse 2.5s ease-in-out infinite',
                '@keyframes badgePulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.08)' },
                },
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                ★ VIP ★
              </Typography>
            </Box>
          )}

          {/* ⭐ Destellos estelares (más naturales) */}
          {isVIP && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: '18%',
                  left: '22%',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#fff',
                  boxShadow: '0 0 10px #fff, 0 0 20px #ffd54f',
                  animation: 'twinkle 2.2s ease-in-out infinite 0.1s',
                  '@keyframes twinkle': {
                    '0%, 100%': { opacity: 0.15 },
                    '50%': { opacity: 1 },
                  },
                  zIndex: 3,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '22%',
                  right: '28%',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#ffd54f',
                  boxShadow: '0 0 8px #ffd54f',
                  animation: 'twinkle 2.9s ease-in-out infinite 0.4s',
                  zIndex: 3,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '70%',
                  left: '15%',
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: '#b2dfdb',
                  boxShadow: '0 0 6px #80cbc4',
                  animation: 'twinkle 3.3s ease-in-out infinite 0.7s',
                  zIndex: 3,
                }}
              />
            </>
          )}
        </Box>

        {/* 📛 Nombre con neón mejorado */}
        <Typography
          variant={isVIP ? 'h2' : 'h4'}
          sx={{
            fontWeight: 'bold',
            mb: isVIP ? 2.8 : 1.8,
            color: isVIP ? '#ffd54f' : '#e0f2f1',
            textShadow: isVIP
              ? `
                  0 0 6px rgba(255,213,79,0.7),
                  0 0 12px rgba(255,213,79,0.5),
                  0 0 20px rgba(76,175,80,0.4)
                `
              : '0 2px 5px rgba(0,0,0,0.6)',
            fontSize: isVIP ? '2.7rem' : '2.1rem',
            lineHeight: 1.15,
            letterSpacing: isVIP ? '1.2px' : '0.8px',
          }}
        >
          
          {current.username}
        </Typography>

        {/* 🎟️ Boletos con efecto premium */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.4,
            bgcolor: isVIP ? 'rgba(0,0,0,0.25)' : 'rgba(56,142,60,0.65)',
            color: '#fff',
            px: isVIP ? 5 : 3.5,
            py: isVIP ? 2.2 : 1.6,
            borderRadius: '56px',
            mb: 2,
            fontSize: isVIP ? '1.9rem' : '1.5rem',
            fontWeight: 'bold',
            boxShadow: isVIP
              ? '0 6px 20px rgba(0,0,0,0.45), inset 0 2px 5px rgba(255,255,255,0.15)'
              : '0 4px 12px rgba(0,0,0,0.35)',
            border: isVIP ? '1px solid rgba(255,255,255,0.1)' : 'none',
            position: 'relative',
            overflow: 'hidden',
            '&::before': isVIP ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,213,79,0.2), transparent)',
              animation: 'shine 3s infinite',
              '@keyframes shine': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(100%)' },
              },
            } : {},
          }}
        >
          <span>🎟️</span>
          <span>
            {current.ticketCount} {current.ticketCount === 1 ? 'boleto' : 'boletos'}
          </span>
        </Box>

        {/* ✅ Aprobado con icono animado */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            color: isVIP ? '#ffd54f' : '#a5d6a7',
          }}
        >
          <CheckIcon
            sx={{
              color: '#4caf50',
              fontSize: isVIP ? 34 : 28,
              animation: isVIP ? 'iconGlow 2s ease-in-out infinite' : 'none',
              '@keyframes iconGlow': {
                '0%, 100%': { 
                  textShadow: '0 0 6px rgba(76,175,80,0.6), 0 0 12px rgba(76,175,80,0.3)' 
                },
                '50%': { 
                  textShadow: '0 0 12px rgba(76,175,80,0.9), 0 0 24px rgba(76,175,80,0.6)' 
                },
              },
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            Chat SRD
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default VmixDisplay;